import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getBestReviews, getChalets } from '../services/dataService';
import { Chalet } from '../types';
import { MarkerMap } from '../components/MarkerMap';

// Icons (lucide-react)
import {
  ArrowRight,
  Star,
  ShieldCheck,
  Heart,
  MapPin,
  Calendar as CalendarIcon,
  Users,
  Search,
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  HomeIcon,
} from 'lucide-react';

// date-fns
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { nl } from 'date-fns/locale';

const HERO_IMAGES = [
  '../images/slider/slider1.jpeg',
  '../images/slider/slider2.jpeg',
  '../images/slider/slider3.jpeg',
  '../images/slider/slider4.jpeg',
  '../images/slider/slider5.jpeg',
];

export const Home: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();


// Map chalets (homepage)
const [mapChalets, setMapChalets] = useState<Chalet[]>([]);
const [mapLoading, setMapLoading] = useState(true);

useEffect(() => {
  let cancelled = false;
  getChalets()
    .then((data) => {
      if (cancelled) return;
      setMapChalets(data);
      setMapLoading(false);
    })
    .catch(() => {
      if (cancelled) return;
      setMapLoading(false);
    });

  return () => {
    cancelled = true;
  };
}, []);	   
  const [currentImage, setCurrentImage] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Search State
  const [searchLocation, setSearchLocation] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(addDays(new Date(), 1));
  const [endDate, setEndDate] = useState<Date | null>(addDays(new Date(), 4));
  const [searchGuests, setSearchGuests] = useState(1);

  // Dropdown States
  const [activeDropdown, setActiveDropdown] = useState<'none' | 'dates' | 'guests'>('none');
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setActiveDropdown('none');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/overview', {
      state: {
        location: searchLocation,
        guests: searchGuests,
        startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
      },
    });
  };
	  
	const [bestReviews, setBestReviews] = useState<BestReview[]>([]);

	useEffect(() => {
	  let mounted = true;
	  (async () => {
		const r = await getBestReviews();
		if (mounted) setBestReviews(r);
	  })();
	  return () => { mounted = false; };
	}, []);

  const handleDateClick = (date: Date) => {
    if (isBefore(date, startOfDay(new Date()))) return;

    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else {
      if (isBefore(date, startDate)) {
        setStartDate(date);
      } else {
        setEndDate(date);
        setActiveDropdown('none');
      }
    }
  };

  const renderCalendar = (baseDate: Date) => {
    const monthStart = startOfMonth(baseDate);
    const monthEnd = endOfMonth(monthStart);
    const start = startOfWeek(monthStart, { weekStartsOn: 1 });
    const end = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });

    return (
      <div className="w-full max-w-[260px] mx-auto lg:mx-0">
        <h4 className="text-xs font-black text-brand-900 mb-4 capitalize tracking-widest text-center">
          {format(monthStart, 'MMMM yyyy', { locale: nl })}
        </h4>
        <div className="grid grid-cols-7 gap-1 text-center text-[9px] font-black text-stone-300 uppercase mb-3">
          {['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'].map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, i) => {
            const isSelected =
              (startDate && isSameDay(day, startDate)) || (endDate && isSameDay(day, endDate));
            const inRange =
              startDate && endDate && isWithinInterval(day, { start: startDate, end: endDate });
            const isPast = isBefore(day, startOfDay(new Date()));
            const isCurrentMonth = day.getMonth() === monthStart.getMonth();

            return (
              <button
                key={i}
                type="button"
                disabled={isPast || !isCurrentMonth}
                onClick={() => handleDateClick(day)}
                className={`
                  h-8 w-8 text-[11px] font-bold rounded-full transition-all flex items-center justify-center relative mx-auto
                  ${!isCurrentMonth ? 'opacity-0 pointer-events-none' : ''}
                  ${isPast ? 'text-stone-200 cursor-not-allowed' : 'hover:bg-brand-50 hover:text-brand-900'}
                  ${isSelected ? 'bg-brand-900 text-white z-10 scale-110 shadow-md' : ''}
                  ${inRange && !isSelected ? 'bg-brand-50 text-brand-900 rounded-none w-full' : ''}
                `}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const heroBg = HERO_IMAGES[currentImage];

  return (
    <div className="flex flex-col min-h-screen">
      {/* HERO SECTION */}
      <section className="relative min-h-screen">
        {/* Background */}
        <div className="absolute inset-0 overflow-hidden bg-stone-900">
            {HERO_IMAGES.map((img, index) => (
            <div
                key={img}
                className={`absolute inset-0 transition-opacity duration-[1500ms] ease-in-out ${
                  index === currentImage ? 'opacity-100' : 'opacity-0'
                }`}
            >
                <img 
                  src={img} 
                  alt="Hero" 
                  className={`w-full h-full object-cover transition-transform duration-[8000ms] ease-out ${
                    index === currentImage ? 'scale-110 translate-y-[-2%]' : 'scale-100 translate-y-0'
                  }`} 
                />
                <div className="absolute inset-0 bg-black/40"></div>
            </div>
            ))}
        </div>

        {/* Hero Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-20 pt-20">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 animate-fade-in-up drop-shadow-lg max-w-4xl leading-tight px-4">
            {t('landing.hero.title')}
          </h1>

          <p className="text-lg md:text-xl text-white/90 mb-12 max-w-2xl animate-fade-in-up delay-100 font-light drop-shadow-md px-4">
            {t('landing.hero.subtitle')}
          </p>

          {/* Premium Pill Search Bar */}
          <div ref={containerRef} className="w-full max-w-5xl px-4 animate-fade-in-up delay-200 relative z-50">
            <form
              onSubmit={handleSearch}
              className="bg-white/95 backdrop-blur-md rounded-[2.5rem] lg:rounded-[5rem] p-2 lg:p-1.5 flex flex-col lg:flex-row items-center shadow-2xl border border-white/20 lg:h-20"
            >
              {/* Location Segment */}
              <div className="flex-[1.2] flex items-center px-6 lg:px-8 py-3 lg:py-0 w-full h-full border-b lg:border-b-0 lg:border-r border-brand-100 hover:bg-brand-50 transition-colors group cursor-pointer rounded-t-[2.5rem] lg:rounded-t-none lg:rounded-l-full">
                <MapPin className="text-brand-400 mr-4 group-hover:text-brand-900 transition-colors" size={20} />
                <div className="flex flex-col items-start w-full overflow-hidden text-left">
                  <label className="text-[10px] font-black uppercase text-stone-400 tracking-widest leading-none mb-1.5">
                    Bestemming
                  </label>
                  <input
                    type="text"
                    placeholder="Welke provincie?"
                    value={searchLocation}
                    onChange={(e) => setSearchLocation(e.target.value)}
                    className="bg-transparent border-none focus:ring-0 p-0 text-sm font-bold text-brand-900 placeholder:text-stone-300 w-full outline-none"
                  />
                </div>
              </div>

              {/* Dates Segment */}
				<div 
					onClick={() => setActiveDropdown('dates')}
					className={`flex-1 flex items-center px-6 lg:px-8 py-3 lg:py-0 w-full h-full border-b lg:border-b-0 lg:border-r border-brand-100 hover:bg-brand-50 transition-colors group cursor-pointer ${activeDropdown === 'dates' ? 'bg-brand-50' : ''}`}
				>
                <CalendarIcon
                  className="text-brand-400 mr-4 group-hover:text-brand-900 transition-colors"
                  size={20}
                />
                <div className="flex flex-col items-start w-full text-left">
                  <label className="text-[10px] font-black uppercase text-stone-400 tracking-widest leading-none mb-1.5">
                    Periode
                  </label>
                  <span className={`text-sm font-bold ${startDate ? 'text-brand-900' : 'text-stone-300'}`}>
                    {startDate ? format(startDate, 'dd MMM', { locale: nl }) : 'Kies data'}
                    {endDate ? ` - ${format(endDate, 'dd MMM', { locale: nl })}` : ''}
                  </span>
                </div>
              </div>

			{/* Guests Segment - Simple Inline Buttons */}
			<div className="flex-1 flex items-center px-6 lg:px-8 py-3 lg:py-0 w-full h-full border-b lg:border-b-0 lg:border-r border-brand-100 hover:bg-brand-50 transition-colors group">
				<Users className="text-brand-400 mr-4 group-hover:text-brand-900 transition-colors flex-shrink-0" size={18} />
				<div className="flex flex-col items-start w-full text-left overflow-hidden">
					<label className="text-[9px] font-black uppercase text-stone-400 tracking-widest leading-none mb-1.5">Gasten</label>
					<div className="flex items-center gap-3">
						<button 
							type="button" 
							onClick={(e) => { e.stopPropagation(); setSearchGuests(Math.max(1, searchGuests - 1)); }}
							className="w-7 h-7 rounded-full border border-brand-200 flex items-center justify-center text-stone-400 hover:text-brand-900 hover:border-brand-900 transition-all active:scale-90 bg-white"
						>
							<Minus size={14} />
						</button>
						<span className="text-sm font-bold text-brand-900 min-w-[12px] text-center">{searchGuests}</span>
						<button 
							type="button" 
							onClick={(e) => { e.stopPropagation(); setSearchGuests(searchGuests + 1); }}
							className="w-6 h-6 rounded-full border border-brand-200 flex items-center justify-center text-stone-400 hover:text-brand-900 hover:border-brand-900 transition-all active:scale-90 bg-white"
						>
							<Plus size={14} />
						</button>
					</div>
				</div>
			</div>

              {/* Search Button Segment */}
              <div className="flex items-center justify-center px-2 py-2 lg:py-1 flex-shrink-0 w-full lg:w-auto h-full">
                <button
                  type="submit"
                  className="bg-brand-900 text-white w-full lg:w-auto px-10 rounded-full lg:rounded-full font-black uppercase tracking-[0.2em] text-[11px] shadow-xl hover:bg-black transition-all flex items-center justify-center gap-3 transform active:scale-95 group h-14 lg:h-full lg:min-h-0"
                >
                  <Search size={18} className="group-hover:scale-110 transition-transform" />
                  Zoeken
                </button>
              </div>
            </form>

            {/* Calendar Dropdown */}
            {activeDropdown === 'dates' && (
              <div className="absolute top-full mt-4 left-0 right-0 lg:left-1/2 lg:-translate-x-1/2 lg:w-[600px] bg-white rounded-[2rem] lg:rounded-[3rem] shadow-2xl p-5 lg:p-7 border border-brand-50 z-[9999] animate-in fade-in zoom-in duration-300 mx-4 lg:mx-0">
                <div className="flex justify-between items-center mb-6 px-2">
                  <h3 className="text-xs font-black uppercase tracking-[0.2em] text-brand-900">
                    Selecteer Reisperiode
                  </h3>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => setCalendarMonth(addMonths(calendarMonth, -1))}
                      className="p-2 hover:bg-brand-50 rounded-full text-brand-900 transition-colors border border-brand-50"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                      className="p-2 hover:bg-brand-50 rounded-full text-brand-900 transition-colors border border-brand-50"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-6 justify-center items-center">
				  <div className="w-full lg:w-auto">
					{renderCalendar(calendarMonth)}
				  </div>
                  <div className="hidden lg:block h-auto w-px bg-brand-50"></div>
                  <div className="hidden lg:block w-full lg:w-auto">{renderCalendar(addMonths(calendarMonth, 1))}</div>
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-5 border-t border-brand-50 px-1 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate(null);
                      setEndDate(null);
                    }}
                    className="text-[10px] font-black text-stone-400 hover:text-brand-900 uppercase tracking-widest transition-colors"
                  >
                    Data wissen
                  </button>
				  <button type="button" onClick={() => setActiveDropdown('none')} className="w-full sm:w-auto bg-brand-900 text-white px-7 py-2.5 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg hover:bg-black transition-colors">Opslaan</button>
                </div>
              </div>
            )}

          </div>

            {/* Slider Navigation */}
            <div className="absolute bottom-10 flex gap-4">
                {HERO_IMAGES.map((_, idx) => (
                    <button 
                        key={idx}
                        onClick={() => setCurrentImage(idx)}
                        className={`h-1 rounded-full transition-all duration-700 ${idx === currentImage ? 'bg-white w-14' : 'bg-white/30 w-6 hover:bg-white/60'}`}
                    />
                ))}
            </div>
        </div>
      </section>

{/*}

      <section className="py-20 lg:py-32 bg-white relative z-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="prose prose-lg prose-brand">
              <h3 className="text-4xl font-serif font-bold text-brand-900 mb-6">{t('landing.about.title')}</h3>
              <p className="text-stone-600 leading-relaxed mb-8">{t('landing.about.text')}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-50 rounded-lg text-brand-600">
                    <Star size={20} />
                  </div>
                  <span className="font-semibold text-brand-900">{t('landing.features.1')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-50 rounded-lg text-brand-600">
                    <Heart size={20} />
                  </div>
                  <span className="font-semibold text-brand-900">{t('landing.features.2')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-50 rounded-lg text-brand-600">
                    <ShieldCheck size={20} />
                  </div>
                  <span className="font-semibold text-brand-900">{t('landing.features.3')}</span>
                </div>
              </div>

              <div className="mt-10">
                <Link
                  to="/overview"
                  className="inline-flex items-center gap-2 font-black uppercase tracking-widest text-[11px] text-brand-900 hover:text-black"
                >
                  Bekijk aanbod <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
{*/}

{/* Locations / Map section */}
<section className="py-20 bg-brand-50">
  <div className="max-w-7xl mx-auto px-6 lg:px-8">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-starts">
      {/* Left copy */}
      <div className="lg:col-span-4">

        <h2 className="mt-5 text-4xl md:text-5xl font-serif font-bold text-brand-900 leading-tight">
          {t('map.title')}
        </h2>

        <p className="mt-4 text-stone-500 text-lg leading-relaxed">
		  {t('map.desc1')}
        </p>
		
        <p className="mt-4 text-stone-500 text-lg leading-relaxed">
		  {t('map.desc2')}
        </p>

	  <div className="mt-10">
		<Link
		  to="/overview"
		  className="inline-flex items-center gap-2 font-black uppercase tracking-widest text-[11px] text-brand-900 hover:text-black"
		>
		  Bekijk aanbod <ArrowRight size={16} />
		</Link>
	  </div>		
		
      </div>

      {/* Right map card */}
      <div className="lg:col-span-8">
          {mapLoading ? (
            <div className="h-[520px] flex items-center justify-center text-stone-400 font-bold">
              Kaart laden…
            </div>
          ) : (
            <MarkerMap chalets={mapChalets} />
          )}
      </div>
    </div>
  </div>
</section>
	  
      <section className="py-24 bg-brand-100 overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="text-center mb-20 space-y-4">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-50 rounded-full border border-brand-100">
                      <Star size={14} className="text-brand-900 fill-brand-900" />
                      <span className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-900">Gastenboek</span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-serif font-bold text-brand-900 leading-tight">{t('guestbook.title')}</h2>
				 {/*}<p className="text-stone-400 text-lg max-w-2xl mx-auto font-light italic">{t('guestbook.desc')}</p>{*/}
              </div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
				  {bestReviews.map((review, idx) => (
					<div
					  key={review.id}
					  className="bg-white p-10 rounded-[2.5rem] border border-stone-100 shadow-sm hover:shadow-xl hover:border-brand-100 transition-all duration-500 group flex flex-col h-full animate-in fade-in slide-in-from-bottom-4 relative"
					  style={{ animationDelay: `${idx * 100}ms`, animationFillMode: 'both' }}
					>
					  <div className="flex justify-between items-start mb-8">
						<div className="flex gap-1">
						  {[...Array(review.rating)].map((_, i) => (
							<Star key={i} size={11} className="fill-brand-400 text-brand-400" />
						  ))}
						</div>
						<div className="px-3 py-1 bg-brand-50 rounded-full flex items-center gap-1.5">
						  <HomeIcon size={10} className="text-brand-400" />
						  <span className="text-[8px] font-black uppercase tracking-widest text-brand-900">
							{review.chalet}
						  </span>
						</div>
					  </div>

					  <p className="text-brand-900 font-serif text-[17px] leading-relaxed mb-10 italic flex-grow group-hover:text-black transition-colors">
						"{review.text}"
					  </p>

					  <div className="flex items-center gap-4 border-t border-stone-50 pt-8 mt-auto">
						<div className="w-11 h-11 rounded-full bg-brand-50 flex items-center justify-center text-brand-900 font-bold text-xs shadow-inner border border-brand-100">
						  {review.name?.charAt(0) ?? "G"}
						</div>
						<div>
						  <h4 className="font-bold text-brand-900 text-sm leading-none mb-1.5">{review.name}</h4>
						  <p className="text-[9px] text-stone-400 uppercase font-black tracking-[0.15em]">
							{/* if you don’t have location, remove it */}
							{review.location ? `${review.location} · ` : ""}
							{review.date ?? ""}
						  </p>
						</div>
					  </div>
					</div>
				  ))}
				</div>
          </div>
      </section>	  
	  
	  
    </div>
  );
};
